import bugsnagClient from 'containers/BugsnagClient';
import dayjs from 'dayjs';
import { formatVolume } from 'utils/helpers';
import { localPoint } from '@visx/event';
import { theme } from 'utils/theme';
import { timeFormat } from 'd3-time-format';

const colors = theme.time_series_line_colors;

export const getSeriesColors = (idx) => colors[idx % colors.length];

const defaultOptions = { skipPrediction: false };

// The throw below reaches Bugsnag without the headers, which identify the
// payload and are not recoverable afterwards. Deduped per series and header pair.
const reportedHeaderMismatches = new Set();

const reportHeaderMismatch = (headers) => {
    try {
        const signature = JSON.stringify(headers);
        if (reportedHeaderMismatches.has(signature)) return;
        bugsnagClient.notify(
            new Error('Mismatched time series header — captured headers'),
            (event) => {
                event.severity = 'warning';
                event.addMetadata('timeSeries', headers);
            },
        );
        // After the call, so a failed report retries.
        reportedHeaderMismatches.add(signature);
    } catch {
        // Reporting must not mask the throw.
    }
};

export const formatSeriesData = (seriesData, options = defaultOptions) => {
    let accessors;
    let header;
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    let predictionMinStartDate = -Infinity;
    const formattedData = [];

    seriesData.forEach(
        ({ id, name, timeSeries, color, predictionColor, ...rest }, idx) => {
            const historical = [];
            const prediction = [];
            timeSeries.forEach((row, idx) => {
                if (idx < 1 && !accessors) {
                    header = row;
                    const normalizeHeaderKey = (key) =>
                        String(key || '')
                            .toLowerCase()
                            .replace(/[^a-z0-9]/g, '');
                    const resolveHeaderValue = (...candidateKeys) => {
                        const normalizedToRawKey = Object.keys(header).reduce(
                            (acc, key) => {
                                acc[normalizeHeaderKey(key)] = key;
                                return acc;
                            },
                            {},
                        );
                        for (const key of candidateKeys) {
                            const normalized = normalizeHeaderKey(key);
                            const rawKey = normalizedToRawKey[normalized];
                            if (rawKey != null) {
                                return header[rawKey];
                            }
                        }
                        return null;
                    };
                    const findHeaderValueByNormalizedPredicate = (
                        predicate,
                    ) => {
                        const rawKey = Object.keys(header).find((key) =>
                            predicate(normalizeHeaderKey(key)),
                        );
                        return rawKey != null ? header[rawKey] : null;
                    };
                    const hashtagsCountHeaderKey =
                        resolveHeaderValue('hashtags_count');
                    const keywordsCountHeaderKey = resolveHeaderValue(
                        'keywords_count',
                        'search_queries_count',
                    );
                    const postsCountHeaderKey =
                        resolveHeaderValue('posts_count');
                    const absWeeklyChangeRankHeaderKey = resolveHeaderValue(
                        'abs_weekly_change_rank',
                    );
                    const absMonthlyChangeRankHeaderKey = resolveHeaderValue(
                        'abs_monthly_change_rank',
                    );
                    const wowChangeHeaderKey =
                        resolveHeaderValue(
                            'non_cum_increase_WoW',
                            'non_cum_increase_WoW_rolling',
                            'WoW_increase',
                            'increase_WoW',
                        ) ??
                        findHeaderValueByNormalizedPredicate(
                            (key) =>
                                key.includes('wow') && key.includes('increase'),
                        );
                    const momChangeHeaderKey =
                        resolveHeaderValue(
                            'non_cum_increase_MoM',
                            'non_cum_increase_MoM_rolling',
                            'MoM_increase',
                            'increase_MoM',
                            'monthly absolute growth',
                            'monthly_absolute_growth',
                            'mom increase',
                            'increase mom',
                        ) ??
                        findHeaderValueByNormalizedPredicate(
                            (key) =>
                                (key.includes('mom') &&
                                    key.includes('increase')) ||
                                (key.includes('monthly') &&
                                    key.includes('absolute') &&
                                    key.includes('growth')),
                        );
                    accessors = {
                        xAccessor: (d) => d[header.date],
                        yAccessor: (d) => d[header.real_volume],
                        predictionAccessor: (d) => d[header.prediction],
                        ...(hashtagsCountHeaderKey !== null && {
                            hashtagsCountAccessor: (d) =>
                                d[hashtagsCountHeaderKey],
                        }),
                        ...(keywordsCountHeaderKey !== null && {
                            keywordsCountAccessor: (d) =>
                                d[keywordsCountHeaderKey],
                        }),
                        ...(postsCountHeaderKey !== null && {
                            postsCountAccessor: (d) => d[postsCountHeaderKey],
                        }),
                        ...(absWeeklyChangeRankHeaderKey !== null && {
                            absWeeklyChangeRankAccessor: (d) =>
                                d[absWeeklyChangeRankHeaderKey],
                        }),
                        ...(absMonthlyChangeRankHeaderKey !== null && {
                            absMonthlyChangeRankAccessor: (d) =>
                                d[absMonthlyChangeRankHeaderKey],
                        }),
                        ...(wowChangeHeaderKey !== null && {
                            wowChangeAccessor: (d) => d[wowChangeHeaderKey],
                        }),
                        ...(momChangeHeaderKey !== null && {
                            momChangeAccessor: (d) => d[momChangeHeaderKey],
                        }),
                    };
                    return;
                }

                if (
                    idx < 1 &&
                    !Object.keys(header).every(
                        (key) => row[key] === header[key],
                    )
                ) {
                    reportHeaderMismatch({
                        seriesId: id ?? null,
                        chartHeader: header,
                        seriesHeader: row,
                    });
                    throw new Error(
                        'Mismatched time series header. All series headers must have the same column positions',
                    );
                }

                if (idx < 1) return;

                const xVal = dayjs(accessors.xAccessor(row)).toDate();
                const yVal = accessors.yAccessor(row);
                if (yVal === null) return;
                const isPrediction = accessors.predictionAccessor(row);
                if (options.skipPrediction && isPrediction) return;

                if (xVal < xMin) xMin = xVal;
                if (xVal > xMax) xMax = xVal;
                if (yVal < yMin) yMin = yVal;
                if (yVal > yMax) yMax = yVal;
                if (!isPrediction && xVal > predictionMinStartDate) {
                    predictionMinStartDate = xVal;
                }

                if (!isPrediction) {
                    historical.push(row);
                    return;
                }

                if (
                    prediction.length < 1 &&
                    historical[historical.length - 1]
                ) {
                    prediction.push(historical[historical.length - 1]);
                }

                prediction.push(row);
            });

            formattedData.push({
                id,
                name,
                color: color || getSeriesColors(idx),
                predictionColor:
                    predictionColor || color || getSeriesColors(idx),
                historical,
                prediction,
                ...rest,
            });
        },
    );

    return {
        accessors,
        xMin,
        xMax,
        yMin,
        yMax: yMax + (yMin === yMax),
        predictionMinStartDate,
        formattedData,
    };
};

export const getPointerMoveHandler =
    ({ data, dataVoronoi, tooltipData, showTooltip, x, y, onTooltipShow }) =>
    (e) => {
        const point = localPoint(e);
        const nearestSeriesDatum = dataVoronoi.map((voronoi) =>
            voronoi.find(point.x, point.y),
        );

        let nearestDatum = null;
        let nearestDatumDistance = Infinity;

        nearestSeriesDatum.forEach((seriesDatum, seriesIdx) => {
            if (!seriesDatum) return null;

            const { data: datum, index: datumIdx } = seriesDatum;
            const datumPoint = {
                x: x(datum),
                y: y(datum),
            };
            const distanceX = Math.abs(datumPoint.x - point.x);
            const distanceY = Math.abs(datumPoint.y - point.y);
            const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

            if (nearestDatumDistance < distance) return null;

            const isPrediction = datum[datum.length - 1] === true;
            const { id, name, color, predictionColor } = data[seriesIdx];
            const strokeColor = isPrediction
                ? predictionColor || color || getSeriesColors(seriesIdx)
                : color || getSeriesColors(seriesIdx);
            nearestDatumDistance = distance;
            nearestDatum = {
                datum,
                datumIdx,
                id,
                name,
                strokeColor,
                distance,
                ...datumPoint,
            };
        });

        if (
            tooltipData &&
            tooltipData.id === nearestDatum.id &&
            tooltipData.datumIdx === nearestDatum.datumIdx
        )
            return null;

        onTooltipShow?.(nearestDatum);

        showTooltip({
            tooltipLeft: nearestDatum.x || 0,
            tooltipTop: nearestDatum.y || 0,
            tooltipData: nearestDatum,
        });
    };

export const getPointerClickHandler =
    ({ data, dataVoronoi, x, y, onClick }) =>
    (e) => {
        const point = localPoint(e);
        const nearestSeriesDatum = dataVoronoi.map((voronoi) =>
            voronoi.find(point.x, point.y),
        );

        let nearestDatum = null;
        let nearestDatumDistance = Infinity;

        nearestSeriesDatum.forEach((seriesDatum, seriesIdx) => {
            if (!seriesDatum) return null;

            const { data: datum, index: datumIdx } = seriesDatum;
            const datumPoint = {
                x: x(datum),
                y: y(datum),
            };
            const distanceX = Math.abs(datumPoint.x - point.x);
            const distanceY = Math.abs(datumPoint.y - point.y);
            const distance = Math.sqrt(
                Math.pow(distanceX, 2) + Math.pow(distanceY, 2),
            );

            if (nearestDatumDistance < distance) return null;

            const isPrediction = datum[datum.length - 1] === true;
            const { id, name, color, predictionColor } = data[seriesIdx];
            const strokeColor = isPrediction
                ? predictionColor || color || getSeriesColors(seriesIdx)
                : color || getSeriesColors(seriesIdx);
            nearestDatumDistance = distance;
            nearestDatum = {
                datum,
                datumIdx,
                id,
                name,
                strokeColor,
                distance,
                ...datumPoint,
            };
        });

        if (nearestDatum && onClick) {
            onClick(nearestDatum);
        }
    };

export const xAxisTickFormat = (v) => timeFormat('%Y')(v);
export const yAxisTickFormat = (v) => formatVolume(v);
